import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Loader2, PackageSearch, CheckCircle2, Clock, FileText, XCircle } from "lucide-react";
import api from "@/lib/api";

const STAGES = [
  { key: "new", label: "Received", icon: Clock },
  { key: "in_review", label: "Under review", icon: PackageSearch },
  { key: "quoted", label: "Options shared", icon: FileText },
  { key: "closed", label: "Closed", icon: CheckCircle2 },
];

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

// Honest tracking: looks up a real enquiry by its PGS reference and shows its status.
// No fabricated vessel/live-shipment data.
export default function TrackShipment({ compact = false }) {
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const track = async (e) => {
    e?.preventDefault();
    if (!ref.trim()) return;
    setLoading(true);
    setResult(null);
    setNotFound(false);
    try {
      const res = await api.get(`/track/${encodeURIComponent(ref.trim())}`);
      if (res.data.found) setResult(res.data);
      else setNotFound(true);
    } catch (e) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const activeIndex = result ? STAGES.findIndex((s) => s.key === result.status) : -1;

  return (
    <div data-testid="track-widget">
      <form onSubmit={track} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9AA6AE]" />
          <input
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="Enter your enquiry reference (e.g. PGS-2607-1234)"
            aria-label="Enquiry reference number"
            data-testid="track-input"
            className="w-full min-h-[52px] rounded-md border border-[#D9E0E4] bg-white pl-10 pr-4 text-[15px] text-navy placeholder:text-[#9AA6AE] focus:outline-none focus:ring-2 focus:ring-teal focus:border-teal"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="track-submit"
          className="min-h-[52px] px-7 rounded-md bg-teal text-white font-semibold hover:bg-[#075652] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Track enquiry
        </button>
      </form>

      <AnimatePresence mode="wait">
        {notFound && (
          <motion.div
            key="nf"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mt-5 rounded-md border border-[#F1C7C3] bg-[#FBEBEA] p-4 flex items-center gap-2"
            data-testid="track-notfound"
          >
            <XCircle className="h-4 w-4 text-[#B42318]" />
            <p className="text-[14px] text-[#B42318]">No enquiry found for that reference. Please check it and try again, or contact our team.</p>
          </motion.div>
        )}

        {result && (
          <motion.div
            key="res"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mt-6 rounded-lg border border-[#D9E0E4] bg-white p-6"
            data-testid="track-result"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Reference</span>
                <p className="font-heading font-extrabold text-teal text-[20px]">{result.reference}</p>
              </div>
              <span className="text-[12px] text-muted-foreground">Submitted {fmt(result.created_at)}</span>
            </div>

            <p className="mt-4 text-[14px] text-navy font-medium">{result.status_label}</p>

            {/* Status stepper */}
            <div className="mt-6 flex items-center">
              {STAGES.map((s, i) => {
                const done = i <= activeIndex;
                const Ico = s.icon;
                return (
                  <React.Fragment key={s.key}>
                    <div className="flex flex-col items-center">
                      <span className={`flex h-9 w-9 items-center justify-center rounded-full border-2 ${done ? "bg-teal border-teal text-white" : "bg-white border-[#D9E0E4] text-[#9AA6AE]"}`}>
                        <Ico className="h-4 w-4" />
                      </span>
                      <span className={`mt-2 text-[11px] font-medium text-center ${done ? "text-navy" : "text-muted-foreground"}`}>{s.label}</span>
                    </div>
                    {i < STAGES.length - 1 && (
                      <span className={`flex-1 h-[2px] mx-1 mb-6 ${i < activeIndex ? "bg-teal" : "bg-[#D9E0E4]"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {result.status_updated_at && (
              <p className="mt-4 text-[12px] text-muted-foreground">Last updated {fmt(result.status_updated_at)}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {!compact && (
        <p className="mt-5 text-[13px] text-muted-foreground leading-relaxed max-w-2xl">
          This shows the status of your enquiry with our team. Detailed sailing schedules and shipment
          milestones are coordinated with the relevant carrier or forwarder partner for your specific booking.
        </p>
      )}
    </div>
  );
}
